import { original } from "@reduxjs/toolkit"
import { FormProvider, useForm } from "react-hook-form"
import { useDispatch } from "react-redux"
import { toast } from "react-toastify"
import { useCallback, useMemo, useState } from "react"
import { CommentsSection } from "../../CommentsSection"
import { DEFAULT_COMMENTS_SORT } from "../../CommentsSection/helpers/commentsSortQuery"
import {
    mergeChildrenIntoParent,
    mergeNewCommentIntoCache,
    patchCommentTextInTree,
    removeCommentFromTree,
    sortCommentsByPublishedAtAsc,
} from "../../CommentsSection/helpers/mergeCommentBranch"
import { CommentDeleteConfirmModalBody } from "../../Modals/CommentDeleteConfirmModalBody"
import { CommentThreadModalBody, type SubmitCommentDetailedResult } from "../../Modals/CommentThreadModalBody"
import modalEmitter from "../../ModalComponent/modalEmitter"
import type { OpenL2ReplyThreadPayload } from "../../CommentsSection/ChildrenComponents/ReplyItem"
import { CommentsType } from "../../../types/common"
import { profileApi } from "../../../api/profile"
import {
    growthToolsApi,
    useAddCommentToGrowthToolMutation,
    useDeleteGrowthToolCommentMutation,
    useGetGrowthToolCommentsQuery,
    useLazyGetGrowthToolCommentsQuery,
    useUpdateGrowthToolCommentMutation,
} from "../../../api/growthTools"
import type { AppDispatch } from "../../../store/store"
import type {
    CommentsSortUi,
    IProjectCommentCountersByType,
    ProjectCommentTabCounts,
} from "../../../api/types/projects"
import type { IGrowthToolResource } from "../../../api/types/growthTools"
import { CURSOR_LIST_PAGE_SIZE } from "../../../consts/api"

type CommentsWrapperProps = {
    growthToolId: number
    cacheKey: number | string
    growthTool?: IGrowthToolResource | null
    onClose?: () => void
}

type CommentsFormValues = {
    commentText: string
    replyText: string
    filterType: CommentsType
    sort: CommentsSortUi
    mobileReplyTarget: { id: number; name: string } | null
}

function resolveGrowthToolCommentTabCounts(
    comments: IGrowthToolResource["counters"]["comments"] | undefined | null
): ProjectCommentTabCounts {
    if (comments && typeof comments === "object") {
        return {
            opinions: Number(comments.opinions) || 0,
            suggestions: Number(comments.suggestions) || 0,
            questions: Number(comments.questions) || 0,
        }
    }
    return { opinions: 0, suggestions: 0, questions: 0 }
}

function incrementGrowthToolCountersForCreatedComment(
    commentsField: IGrowthToolResource["counters"]["comments"],
    createdType: string | undefined
): IGrowthToolResource["counters"]["comments"] {
    if (typeof commentsField === "number") {
        return commentsField + 1
    }
    if (!commentsField || typeof commentsField !== "object") return commentsField
    const c = commentsField as IProjectCommentCountersByType
    c.total = (Number(c.total) || 0) + 1
    const t = (createdType ?? "").toLowerCase()
    if (t === "opinion") c.opinions = (Number(c.opinions) || 0) + 1
    else if (t === "suggestion") c.suggestions = (Number(c.suggestions) || 0) + 1
    else if (t === "question") c.questions = (Number(c.questions) || 0) + 1
    return c
}

function decrementGrowthToolCountersForDeletedComment(
    commentsField: IGrowthToolResource["counters"]["comments"],
    deletedType: string | undefined
): IGrowthToolResource["counters"]["comments"] {
    if (typeof commentsField === "number") {
        return Math.max(0, commentsField - 1)
    }
    if (!commentsField || typeof commentsField !== "object") return commentsField
    const c = commentsField as IProjectCommentCountersByType
    c.total = Math.max(0, (Number(c.total) || 0) - 1)
    const t = (deletedType ?? "").toLowerCase()
    if (t === "opinion" || t === "opinions") c.opinions = Math.max(0, (Number(c.opinions) || 0) - 1)
    else if (t === "suggestion" || t === "suggestions") c.suggestions = Math.max(0, (Number(c.suggestions) || 0) - 1)
    else if (t === "question" || t === "questions") c.questions = Math.max(0, (Number(c.questions) || 0) - 1)
    return c
}

export const CommentsWrapper = ({ growthToolId, cacheKey, growthTool, onClose }: CommentsWrapperProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const [fetchCommentBranch] = useLazyGetGrowthToolCommentsQuery()
    const [loadingParentId, setLoadingParentId] = useState<number | null>(null)
    const methods = useForm<CommentsFormValues>({
        defaultValues: {
            commentText: "",
            replyText: "",
            sort: DEFAULT_COMMENTS_SORT,
            filterType: CommentsType.opinions,
            mobileReplyTarget: null as { id: number; name: string } | null,
        },
    })

    const currentFilter = methods.watch("filterType") as CommentsType
    const currentSort = methods.watch("sort") as CommentsSortUi
    const { data: profile } = profileApi.useGetProfileQuery()
    const currentUserId = profile?.id ?? null
    const [addCommentToGrowthTool, { isLoading: isSubmittingComment }] = useAddCommentToGrowthToolMutation()
    const [updateComment, { isLoading: isUpdatingComment }] = useUpdateGrowthToolCommentMutation()
    const [deleteCommentMutation] = useDeleteGrowthToolCommentMutation()
    const canComment = growthTool?.viewer?.permissions?.can_comment !== false
    const tabCounts = useMemo(
        () => resolveGrowthToolCommentTabCounts(growthTool?.counters?.comments),
        [growthTool?.counters?.comments],
    )

    const commentsListCacheKey = useMemo(
        () => ({
            growthToolId,
            type: currentFilter,
            limit: CURSOR_LIST_PAGE_SIZE,
            sort: currentSort,
        }),
        [growthToolId, currentFilter, currentSort],
    )

    const { data: commentsData, isLoading: isCommentsLoading, isError: isCommentsError } =
        useGetGrowthToolCommentsQuery(commentsListCacheKey, { skip: !growthToolId })

    const submitCommentDetailed = useCallback(
        async ({ text, parentId }: { text: string; parentId?: number }): Promise<SubmitCommentDetailedResult> => {
            const trimmed = text.trim()
            if (!trimmed || !growthToolId) return { ok: false }
            const body = parentId ? { text: trimmed, parent_id: parentId } : { text: trimmed }
            try {
                const created = await addCommentToGrowthTool({
                    growthToolId,
                    type: currentFilter,
                    body,
                }).unwrap()

                dispatch(
                    growthToolsApi.util.updateQueryData(
                        "getGrowthToolComments",
                        commentsListCacheKey,
                        (draft) =>
                            mergeNewCommentIntoCache(
                                original(draft) ?? [...draft],
                                created,
                                created.parent_id ?? null,
                                currentSort,
                            )
                    )
                )

                dispatch(
                    growthToolsApi.util.updateQueryData("getGrowthTool", { id: cacheKey }, (draft) => {
                        if (!draft) return
                        draft.counters.comments = incrementGrowthToolCountersForCreatedComment(
                            draft.counters.comments,
                            created.type,
                        )
                    })
                )

                if (parentId) {
                    methods.setValue("replyText", "")
                    methods.setValue("mobileReplyTarget", null)
                    methods.setValue("commentText", "")
                } else {
                    methods.setValue("commentText", "")
                }
                return { ok: true, created }
            } catch {
                toast.error("Не удалось отправить комментарий")
                return { ok: false }
            }
        },
        [
            growthToolId,
            currentFilter,
            currentSort,
            addCommentToGrowthTool,
            dispatch,
            methods,
            commentsListCacheKey,
            cacheKey,
        ],
    )

    const onSubmitComment = async ({ text, parentId }: { text: string; parentId?: number }): Promise<boolean> => {
        const result = await submitCommentDetailed({ text, parentId })
        return result.ok
    }

    const onUpdateComment = useCallback(
        async (commentId: number, text: string): Promise<boolean> => {
            try {
                const updated = await updateComment({ commentId, body: { text } }).unwrap()
                dispatch(
                    growthToolsApi.util.updateQueryData(
                        "getGrowthToolComments",
                        commentsListCacheKey,
                        (draft) => patchCommentTextInTree(original(draft) ?? [...draft], commentId, updated)
                    )
                )
                return true
            } catch {
                toast.error("Не удалось сохранить изменения")
                return false
            }
        },
        [updateComment, dispatch, commentsListCacheKey],
    )

    const onDeleteComment = useCallback(
        (commentId: number): Promise<boolean> =>
            new Promise((resolve) => {
                let settled = false
                const settle = (value: boolean) => {
                    if (settled) return
                    settled = true
                    resolve(value)
                }

                const performDelete = async (): Promise<boolean> => {
                    try {
                        await deleteCommentMutation({ commentId }).unwrap()
                        dispatch(
                            growthToolsApi.util.updateQueryData(
                                "getGrowthToolComments",
                                commentsListCacheKey,
                                (draft) => removeCommentFromTree(original(draft) ?? [...draft], commentId)
                            )
                        )
                        dispatch(
                            growthToolsApi.util.updateQueryData("getGrowthTool", { id: cacheKey }, (draft) => {
                                if (!draft) return
                                draft.counters.comments = decrementGrowthToolCountersForDeletedComment(
                                    draft.counters.comments,
                                    currentFilter,
                                )
                            })
                        )
                        toast.success("Комментарий удалён")
                        settle(true)
                        return true
                    } catch {
                        toast.error("Не удалось удалить комментарий")
                        return false
                    }
                }

                modalEmitter.emitOpenModal({
                    dialog: {
                        title: "Удалить комментарий?",
                        body: (
                            <CommentDeleteConfirmModalBody
                                onConfirm={performDelete}
                                onCancel={() => settle(false)}
                            />
                        ),
                        modalToCentrPosition: true,
                        afterCloseCallBack: () => settle(false),
                    },
                    closeAtion: { visible: false },
                    confirmAtion: { visible: false },
                })
            }),
        [deleteCommentMutation, dispatch, commentsListCacheKey, cacheKey, currentFilter],
    )

    const handleOpenReplyThread = useCallback(
        (p: OpenL2ReplyThreadPayload) => {
            const initialComposerTarget =
                p.startReplyComposer === true
                    ? {
                          parentId: p.anchorCommentId,
                          firstName: p.anchorFirstName ?? "",
                          lastName: p.anchorLastName ?? "",
                      }
                    : undefined

            modalEmitter.emitOpenModal({
                dialog: {
                    title: "Ответы",
                    body: (
                        <CommentThreadModalBody
                            growthToolId={growthToolId}
                            commentType={currentFilter}
                            anchorL2Id={p.anchorCommentId}
                            excerpt={p.excerpt}
                            currentUserId={currentUserId}
                            submitCommentDetailed={submitCommentDetailed}
                            onUpdateComment={onUpdateComment}
                            isUpdatingComment={isUpdatingComment}
                            isSubmittingComment={isSubmittingComment}
                            initialComposerTarget={initialComposerTarget}
                            highlightCommentId={p.highlightCommentId}
                        />
                    ),
                    tallCentered: true,
                },
                closeAtion: { visible: false },
                confirmAtion: { visible: false },
            })
        },
        [
            growthToolId,
            currentFilter,
            currentUserId,
            submitCommentDetailed,
            onUpdateComment,
            isUpdatingComment,
            isSubmittingComment,
        ],
    )

    const loadMoreReplies = useCallback(
        async (parentId: number) => {
            setLoadingParentId(parentId)
            try {
                const newNodes = await fetchCommentBranch({
                    growthToolId,
                    type: currentFilter,
                    parentId,
                    limit: CURSOR_LIST_PAGE_SIZE,
                }).unwrap()
                const sortedNewNodes = sortCommentsByPublishedAtAsc(newNodes)

                dispatch(
                    growthToolsApi.util.updateQueryData(
                        "getGrowthToolComments",
                        commentsListCacheKey,
                        (draft) =>
                            mergeChildrenIntoParent(
                                original(draft) ?? [...draft],
                                parentId,
                                sortedNewNodes
                            )
                    )
                )
            } catch {
                toast.error("Не удалось загрузить ответы")
            } finally {
                setLoadingParentId(null)
            }
        },
        [growthToolId, currentFilter, dispatch, fetchCommentBranch, commentsListCacheKey],
    )

    return (
        <FormProvider {...methods}>
           <CommentsSection
               onClose={onClose}
               commentsData={commentsData}
               onSubmitComment={onSubmitComment}
               isSubmittingComment={isSubmittingComment}
               isCommentsLoading={isCommentsLoading}
               isCommentsError={isCommentsError}
               loadMoreReplies={loadMoreReplies}
               loadingParentId={loadingParentId}
               currentUserId={currentUserId}
               onUpdateComment={onUpdateComment}
               isUpdatingComment={isUpdatingComment}
               onDeleteComment={onDeleteComment}
               canComment={canComment}
               onOpenReplyThread={handleOpenReplyThread}
               commentForbiddenText="Вам запрещено комментировать этот инструмент."
               tabCounts={tabCounts}
               commentReactionCache={{ kind: "growthTool", growthToolId, type: currentFilter, sort: currentSort }}
           />
        </FormProvider>
    )
}
